import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canInAnyScope } from '@testflow/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PanelFieldInput } from '../../components/panel-fields.js';
import { catalogApi } from '../../lib/catalog-api.js';
import { plansApi } from '../../lib/plans-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { type TestCaseDraft, emptyTestCaseDraft, testCaseFields } from './testcase-fields.js';

// #28 / Testrunde-1-Finding 2: Bisher legte der Baum einen Testfall mit nur dem Titel an und der
// Nutzer klickte danach sechsmal rechts nach. Hier sind alle Felder gleichzeitig eingabebereit
// und es gibt EIN Speichern. Die Felder kommen aus derselben Liste wie die Detailansicht
// (testcase-fields.ts) — Anlegen und Bearbeiten koennen deshalb nicht auseinanderdriften.

export function TestCasePanel({
  projectId,
  processId,
  onCreated,
  onCancel,
  onDirtyChange,
}: {
  projectId: string;
  processId: string;
  onCreated: (testCaseId: string) => void;
  onCancel: () => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const { user } = useScopedUser(projectId);
  // I3: Speichern war clientseitig nicht an catalog.edit gebunden — Muster wie
  // step-table.tsx/glossary-tab.tsx.
  const canEdit = user ? canInAnyScope(user, 'catalog.edit') : false;
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<TestCaseDraft>(emptyTestCaseDraft);
  const [error, setError] = useState<string | null>(null);

  const membersQuery = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => plansApi.members(projectId),
  });

  const dirty = JSON.stringify(draft) !== JSON.stringify(emptyTestCaseDraft);
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const create = useMutation({
    mutationFn: (d: TestCaseDraft) =>
      catalogApi.createTestCase(projectId, { process_id: processId, ...d }),
    meta: { successToast: 'feedback.created', errorToast: false },
    // #143: neuer Testfall soll im gefilterten Baum (Volliste) auftauchen, ohne Neuladen.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tcs-search', projectId] }),
  });

  const save = async () => {
    // Zwei Dinge werden HIER geprueft, nicht nur am disabled-Knopf: das Recht (canEdit) und der
    // Titel-Pflichtfeld-Guard. Enter loest denselben Weg aus wie der Knopf, und ein Guard nur am
    // Knopf liesse die Tastatur trotzdem einen Request schicken — ohne Recht (Betrachter, 403 vom
    // Server) oder mit leerem Pflichtfeld. Heute nur ueber "+ Testfall" gegatet erreichbar, aber
    // dieselbe Falle wie bei process-panel.tsx bei der naechsten Aenderung.
    if (!canEdit || create.isPending || draft.title_de.trim() === '') return;
    setError(null);
    try {
      const created = await create.mutateAsync(draft);
      // Erst den Dirty-Zustand loeschen, dann die Auswahl wechseln — sonst blockiert die
      // Dirty-Regel in ProcessesPanel den eigenen Sprung auf den neuen Testfall.
      onDirtyChange(false);
      onCreated(created.test_case_id);
    } catch {
      // Panel bleibt offen, Eingaben bleiben stehen (Spec Kapitel 4.1).
      setError(t('catalog.errors.generic'));
    }
  };

  // Enter speichert, Escape verwirft; im mehrzeiligen Feld macht Enter einen Zeilenumbruch und
  // Strg+Enter speichert. stopPropagation, damit nichts an globale Handler durchschlaegt.
  const onKeyDown = (e: React.KeyboardEvent) => {
    const multiline = (e.target as HTMLElement).tagName === 'TEXTAREA';
    if (e.key === 'Enter' && (!multiline || e.ctrlKey)) {
      e.preventDefault();
      e.stopPropagation();
      void save();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onDirtyChange(false);
      onCancel();
    }
  };

  const fields = testCaseFields((k) => t(k), membersQuery.data ?? []);

  return (
    // Tastaturmuster des Panels, identisch zu EditRow in catalog-table.tsx.
    <div className="space-y-4 p-6" onKeyDown={onKeyDown}>
      <h2 className="text-sm font-bold text-text-primary">{t('catalog.actions.addTestCase')}</h2>

      <div className="grid grid-cols-2 gap-4 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        {fields.map((field, index) => (
          // Kein <label>: PanelFieldInput setzt aria-label bereits selbst (Task 3), ein
          // umschliessendes <label> ohne htmlFor waere eine zweite, ungebundene Benennung.
          <div key={field.key} className="space-y-1">
            <span className="block text-[11px] font-bold uppercase tracking-wide text-text-muted">
              {field.labelKey}
            </span>
            <PanelFieldInput
              field={field}
              draft={draft}
              onChange={setDraft}
              autoFocus={index === 0}
            />
          </div>
        ))}
      </div>

      {error && <div className="text-xs text-status-fail">{error}</div>}

      <div className="flex gap-2">
        {canEdit && (
          <button
            type="button"
            onClick={() => void save()}
            disabled={create.isPending || draft.title_de.trim() === ''}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {t('catalog.actions.save')}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            onDirtyChange(false);
            onCancel();
          }}
          className="px-3 py-1.5 text-sm text-text-muted hover:text-text-body"
        >
          {t('catalog.actions.discard')}
        </button>
      </div>
    </div>
  );
}
